"use strict";
(() => {
  const DEFAULT_LIMITS = { sph: { min: -10, max: 10 }, cyl: { min: -10, max: 10 } };
  const SUM_LIMIT = 10;
  const isObject = (value) => typeof value === "object" && value !== null;

  const parseJson = (value) => {
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const normalizeRange = (range, fallback) => {
    if (!isObject(range)) return fallback;
    const min = range.min;
    const max = range.max;
    if (typeof min !== "number" || typeof max !== "number" || !Number.isFinite(min) || !Number.isFinite(max) || min > max) {
      return fallback;
    }
    return { min, max };
  };

  const parseLimits = (value) => {
    const parsed = parseJson(value);
    if (!isObject(parsed)) return DEFAULT_LIMITS;
    return {
      sph: normalizeRange(parsed.sph, DEFAULT_LIMITS.sph),
      cyl: normalizeRange(parsed.cyl, DEFAULT_LIMITS.cyl)
    };
  };


  const LOCK_NAME = "__twoNuRxCartLock";
  const getRxLock = () => {
    if (!window[LOCK_NAME] || typeof window[LOCK_NAME] !== "object") {
      window[LOCK_NAME] = { inFlight: false, lastKey: "", lastAt: 0, lastGroupId: "" };
    }
    return window[LOCK_NAME];
  };
  const DBLCLICK_MS = 2000;
  const clearRxButtons = () => {
    document.querySelectorAll(".prescription__button .button, [button-skip-prescription]").forEach((btn) => {
      btn.removeAttribute("aria-disabled");
      btn.removeAttribute("disabled");
      btn.classList.remove("loading");
      btn.querySelector?.(".loading-overlay")?.classList.add("hidden");
    });
  };
  const acquireRxLock = (variantId) => {
    const lock = getRxLock();
    const now = Date.now();
    const key = String(variantId || "");
    if (lock.inFlight) return false;
    if (key && key === lock.lastKey && now - lock.lastAt < DBLCLICK_MS) return false;
    lock.inFlight = true;
    lock.lastKey = key;
    lock.lastAt = now;
    return true;
  };
  const releaseRxLock = (keepCooldown) => {
    const lock = getRxLock();
    lock.inFlight = false;
    lock.lastAt = Date.now();
    if (!keepCooldown) {
      lock.lastKey = "";
      lock.lastAt = 0;
    }
  };
  const resetRxSession = () => {
    releaseRxLock(false);
    clearRxButtons();
  };
  const bindRxGuards = () => {
    if (window.__twoNuA2RxGuardsBound) return;
    window.__twoNuA2RxGuardsBound = true;
    const isLocked = () => Boolean(getRxLock().inFlight);
    document.addEventListener("click", (event) => {
      const button = event.target instanceof Element
        ? event.target.closest(".prescription__button .button, [button-skip-prescription]")
        : null;
      if (!button || !isLocked()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    document.addEventListener("dblclick", (event) => {
      const button = event.target instanceof Element
        ? event.target.closest(".prescription__button .button")
        : null;
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    document.addEventListener("pointerdown", (event) => {
      const hit = event.target instanceof Element
        ? event.target.closest(".prescription__date-field, .prescription__date-title, .prescription__date-icon, #date-of-issue")
        : null;
      if (!hit) return;
      const input = document.querySelector("#date-of-issue");
      if (!(input instanceof HTMLInputElement) || typeof input.showPicker !== "function") return;
      event.preventDefault();
      try {
        input.showPicker();
        input.dataset.a2PickerMethod = "showPicker";
        input.dataset.a2PickerOpened = String(Date.now());
      } catch (error) {
        input.dataset.a2PickerMethod = "showPicker-throw";
        input.dataset.a2PickerError = String(error && error.message);
        input.focus({ preventScroll: true });
      }
    }, true);
    ["activate-pdp-prescription", "activate-plp-prescription"].forEach((eventName) => {
      window.addEventListener(eventName, () => {
        resetRxSession();
      }, true);
    });
  };
  bindRxGuards();

  const openCartAfterSuccess = () => {
    const opened =
      (typeof window.twoNuOpenCartDrawer === "function" && window.twoNuOpenCartDrawer()) ||
      (typeof window.twoNuOpenDockCart === "function" && window.twoNuOpenDockCart());

    if (typeof window.twoNuRefreshDockCart === "function") {
      window.twoNuRefreshDockCart();
      window.setTimeout(() => window.twoNuRefreshDockCart(), 260);
      window.setTimeout(() => window.twoNuRefreshDockCart(), 900);
    }

    document.dispatchEvent(new CustomEvent("cart:refresh"));
    return Boolean(opened);
  };

  Alpine.data("prescription", () => ({
    price: undefined,
    isPrescriptionOpen: false,
    currentId: "",
    skipPrescription: false,
    baseUrl: "",
    skipMessage: "",
    prescriptionLimits: DEFAULT_LIMITS,
    disableScroll: window.utils.disableScroll,
    enableScroll: window.utils.enableScroll,
    a2Submitting: false,
    cartError: "",
    prescriptionOptions: {
      rightSphereOption: "0.00",
      rightCylinderOption: "0.00",
      rightAxisOption: "0",
      leftSphereOption: "0.00",
      leftCylinderOption: "0.00",
      leftAxisOption: "0",
      dateOption: "",
      checkboxAgree: false
    },
    eyePrescriptionOption: {
      right: { sumValue: 0, isAxisDisabled: true },
      left: { sumValue: 0, isAxisDisabled: true },
      isError: false,
      isPositiveAndNegativeValue: false,
      isOnlyCylinderIsFill: false,
      isContinueDisabled: false,
      isDateEmpty: false,
      isDateInvalid: false,
      isCheckboxAgree: true
    },
    variant: "",
    prescriptionValues: {
      left: { sphere: "", cylynder: "", axis: "" },
      right: { sphere: "", cylynder: "", axis: "" },
      pd: ""
    },
    init() {
      const fallback = "We’ll ask for your prescription after checkout.";
      this.skipMessage = this.$el.dataset.skipMessage ?? fallback;
    },
    applyPrescriptionLimits() {
      const root = document.querySelector(".prescription--js");
      if (!(root instanceof HTMLElement)) return;

      const applySelect = (select, allowedRange) => {
        const isAllowed = (value) => Number.isFinite(value) && value >= allowedRange.min && value <= allowedRange.max;
        for (const option of select.options) {
          const allowed = isAllowed(Number(option.value));
          option.disabled = !allowed;
          option.hidden = !allowed;
        }
        const selected = select.selectedOptions[0];
        if (selected && !selected.disabled) return;
        const fallbackValue = select.dataset.default;
        if (fallbackValue && isAllowed(Number(fallbackValue))) {
          select.value = fallbackValue;
          return;
        }
        const firstAllowed = Array.from(select.options).find((option) => !option.disabled);
        if (firstAllowed) select.value = firstAllowed.value;
      };

      const targets = [
        { selector: ".right-sphere-select--js select", allowedRange: this.prescriptionLimits.sph },
        { selector: ".left-sphere-select--js select", allowedRange: this.prescriptionLimits.sph },
        { selector: ".right-cylinder-select--js select", allowedRange: this.prescriptionLimits.cyl },
        { selector: ".left-cylinder-select--js select", allowedRange: this.prescriptionLimits.cyl }
      ];

      for (const { selector, allowedRange } of targets) {
        const select = root.querySelector(selector);
        if (select instanceof HTMLSelectElement) applySelect(select, allowedRange);
      }

      this.prescriptionOptionsValidation();
    },
    resetPrescription() {
      this.skipPrescription = false;
      this.prescriptionOptions.rightSphereOption = "0.00";
      this.prescriptionOptions.rightCylinderOption = "0.00";
      this.prescriptionOptions.rightAxisOption = "0";
      this.prescriptionOptions.leftSphereOption = "0.00";
      this.prescriptionOptions.leftCylinderOption = "0.00";
      this.prescriptionOptions.leftAxisOption = "0";
      this.prescriptionOptions.dateOption = "";
      this.prescriptionOptions.checkboxAgree = false;
      this.eyePrescriptionOption.right.sumValue = 0;
      this.eyePrescriptionOption.right.isAxisDisabled = true;
      this.eyePrescriptionOption.left.sumValue = 0;
      this.eyePrescriptionOption.left.isAxisDisabled = true;
      this.eyePrescriptionOption.isError = false;
      this.eyePrescriptionOption.isPositiveAndNegativeValue = false;
      this.eyePrescriptionOption.isOnlyCylinderIsFill = false;
      this.eyePrescriptionOption.isContinueDisabled = false;
      this.eyePrescriptionOption.isDateEmpty = false;
      this.eyePrescriptionOption.isDateInvalid = false;
      this.eyePrescriptionOption.isCheckboxAgree = true;
      this.cartError = "";
      this.a2Submitting = false;
    },
    isDateInvalidFunc(value) {
      const date = new Date(value);
      const now = new Date();
      if (date > now) return true;
      const oldest = new Date();
      oldest.setFullYear(now.getFullYear() - 2);
      return date < oldest;
    },
    handleCheckDate() {
      const value = this.prescriptionOptions.dateOption;
      this.eyePrescriptionOption.isDateEmpty = value === "";
      const invalid = !!this.isDateInvalidFunc(value ? new Date(value) : new Date());
      this.eyePrescriptionOption.isDateInvalid = invalid;
      this.eyePrescriptionOption.isContinueDisabled = invalid;
    },
    handleCheckboxAgree() {
      this.eyePrescriptionOption.isCheckboxAgree = this.prescriptionOptions.checkboxAgree;
    },
    openDatePicker() {
      const input = this.$root.querySelector("#date-of-issue") || document.getElementById("date-of-issue");
      if (!(input instanceof HTMLInputElement)) return;
      input.dataset.a2PickerAttempt = "1";
      input.dataset.a2PickerMethod = "";
      input.focus({ preventScroll: true });
      if (typeof input.showPicker === "function") {
        try {
          input.showPicker();
          input.dataset.a2PickerMethod = "showPicker";
          return;
        } catch (error) {
          input.dataset.a2PickerMethod = "showPicker-throw";
        }
      }
      input.click();
      input.dataset.a2PickerMethod = input.dataset.a2PickerMethod || "click";
    },
    checkContinue() {
      if (this.a2Submitting || getRxLock().inFlight) return;
      if (
        this.prescriptionOptions.rightSphereOption === "0.00" &&
        this.prescriptionOptions.rightCylinderOption === "0.00" &&
        this.prescriptionOptions.rightAxisOption === "0" &&
        this.prescriptionOptions.leftSphereOption === "0.00" &&
        this.prescriptionOptions.leftCylinderOption === "0.00" &&
        this.prescriptionOptions.leftAxisOption === "0"
      ) {
        this.openPrescriptionLaterModal();
        return;
      }
      this.prescriptionOptionsValidation();
      this.handleCheckDate();
      this.handleCheckboxAgree();
      if (
        this.eyePrescriptionOption.isError ||
        this.eyePrescriptionOption.isOnlyCylinderIsFill ||
        this.eyePrescriptionOption.isDateEmpty ||
        this.eyePrescriptionOption.isDateInvalid ||
        !this.eyePrescriptionOption.isCheckboxAgree
      ) {
        this.eyePrescriptionOption.isContinueDisabled = true;
        return;
      }
      this.a2Submitting = true;
      this.addToCart();
    },
    prescriptionOptionsValidation() {
      const rightSphere = Number(this.prescriptionOptions.rightSphereOption);
      const rightCylinder = Number(this.prescriptionOptions.rightCylinderOption);
      const leftSphere = Number(this.prescriptionOptions.leftSphereOption);
      const leftCylinder = Number(this.prescriptionOptions.leftCylinderOption);
      const rightAxis = Number(this.prescriptionOptions.rightAxisOption);
      const leftAxis = Number(this.prescriptionOptions.leftAxisOption);

      if (rightCylinder === 0) this.prescriptionOptions.rightAxisOption = "0";
      if (leftCylinder === 0) this.prescriptionOptions.leftAxisOption = "0";

      this.eyePrescriptionOption.right.sumValue = Math.abs(rightSphere) + Math.abs(rightCylinder);
      this.eyePrescriptionOption.left.sumValue = Math.abs(leftSphere) + Math.abs(leftCylinder);

      const sphOk =
        rightSphere >= this.prescriptionLimits.sph.min &&
        rightSphere <= this.prescriptionLimits.sph.max &&
        leftSphere >= this.prescriptionLimits.sph.min &&
        leftSphere <= this.prescriptionLimits.sph.max;
      const cylOk =
        rightCylinder >= this.prescriptionLimits.cyl.min &&
        rightCylinder <= this.prescriptionLimits.cyl.max &&
        leftCylinder >= this.prescriptionLimits.cyl.min &&
        leftCylinder <= this.prescriptionLimits.cyl.max;
      const overSum =
        this.eyePrescriptionOption.right.sumValue > SUM_LIMIT ||
        this.eyePrescriptionOption.left.sumValue > SUM_LIMIT;

      this.eyePrescriptionOption.isError = overSum || !sphOk || !cylOk;
      this.eyePrescriptionOption.isPositiveAndNegativeValue =
        (rightSphere > 0 && leftSphere < 0) ||
        (rightSphere < 0 && leftSphere > 0) ||
        (rightCylinder > 0 && leftCylinder < 0) ||
        (rightCylinder < 0 && leftCylinder > 0);
      this.eyePrescriptionOption.right.isAxisDisabled = rightCylinder === 0;
      this.eyePrescriptionOption.left.isAxisDisabled = leftCylinder === 0;
      this.eyePrescriptionOption.isOnlyCylinderIsFill =
        (rightCylinder !== 0 && rightAxis === 0) ||
        (leftCylinder !== 0 && leftAxis === 0);
      this.eyePrescriptionOption.isContinueDisabled = false;
    },
    takeInputs() {
      const root = document.querySelector(".prescription--js");
      const selects = root?.querySelectorAll(".select");
      selects?.forEach((node) => this.setPrescriptionValue(node));
    },
    clearInputs() {
      const root = document.querySelector(".prescription--js");
      const selects = root?.querySelectorAll(".select");
      selects?.forEach((node) => {
        const select = node.children[0];
        if (select instanceof HTMLSelectElement) {
          const fallback = select.dataset.default;
          if (fallback) select.value = fallback;
        }
      });
    },
    activatePdpPrescription({ id, price, baseUrl, prescriptionLimits }) {
      if (!id) return;
      this.currentId = id;
      this.price = price;
      this.prescriptionLimits = parseLimits(prescriptionLimits);
      this.openPrescription();
      this.applyPrescriptionLimits();
      if (baseUrl) this.baseUrl = baseUrl;
    },
    activatePlpPrescription(event) {
      const { baseUrl, id, prescriptionLimits } = event.detail;
      if (!id) return;
      this.baseUrl = baseUrl;
      this.currentId = id;
      this.prescriptionLimits = parseLimits(prescriptionLimits);
      this.openPrescription();
      this.applyPrescriptionLimits();
    },
    addToCart() {
      return (async () => {
        if (!acquireRxLock(this.currentId)) return;
        this.a2Submitting = true;
        this.cartError = "";

        if (this.isPrescriptionOpen) this.takeInputs();

        const groupId = window.utils.generateUUID();
        let url = window.location.href;
        if (this.baseUrl) {
          const params = new URLSearchParams();
          params.set("variant", this.currentId);
          url = `${this.baseUrl}?${params.toString()}`;
        }

        const hasSphere = this.prescriptionValues.left.sphere || this.prescriptionValues.right.sphere;
        let propertiesExtra = {};
        if (hasSphere && !this.skipPrescription) {
          const leftSphere = hasSphere ? this.prescriptionValues.left.sphere : "";
          const rightSphere = hasSphere ? this.prescriptionValues.right.sphere : "";
          const leftAxis = hasSphere ? `/${this.prescriptionValues.left.axis}` : "";
          const rightAxis = hasSphere ? `/${this.prescriptionValues.right.axis}` : "";
          const leftCyl = hasSphere ? `${this.prescriptionValues.left.cylynder}${leftAxis}` : "";
          const rightCyl = hasSphere ? `${this.prescriptionValues.right.cylynder}${rightAxis}` : "";
          propertiesExtra = {
            _EYE_1: "Right",
            _SPH_Right: rightSphere,
            _CYL_Right: rightCyl,
            _EYE_2: "Left",
            _SPH_Left: leftSphere,
            _CYL_Left: leftCyl,
            _PD: this.prescriptionValues.pd
          };
        }

        const items = [{
          id: this.currentId,
          quantity: 1,
          properties: Object.assign({
            _groupId: groupId,
            _url: url,
            _page_of_purchase: window.location.pathname,
            _isAddon: false,
            _isUpgradable: true,
            _currentAvailableUpgradeVariants: this.currentAvailableUpgradeVariants
          }, propertiesExtra)
        }];

        if (this.skipPrescription) items[0].properties.Message = this.skipMessage;

        const trigger = this.skipPrescription
          ? document.querySelector("[button-skip-prescription]")
          : this.$el.querySelector?.(".prescription__button .button") || this.$el;

        try {
          document.querySelectorAll(".prescription__button .button, [button-skip-prescription]").forEach((btn) => {
            btn.setAttribute("aria-disabled", "true");
            btn.classList.add("loading");
            btn.querySelector?.(".loading-overlay")?.classList.remove("hidden");
          });

          const response = await fetch(window.routes.cart_add_url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items })
          });

          let payload = null;
          try {
            payload = await response.json();
          } catch (error) {
            payload = null;
          }

          const failed =
            !response.ok ||
            (payload && typeof payload.status === "number" && payload.status >= 400) ||
            (payload && payload.description && !payload.items && !payload.item_count && payload.status);

          if (failed) {
            this.cartError = (payload && (payload.description || payload.message)) || "Could not add to cart. Please try again.";
            this.eyePrescriptionOption.isContinueDisabled = false;
            this.a2Submitting = false;
            releaseRxLock(false);
            clearRxButtons();
            return;
          }

          this.a2Submitting = false;
          releaseRxLock(true);
          clearRxButtons();
          this.closePrescriptionAfterSuccess();
          window.requestAnimationFrame(() => openCartAfterSuccess());
        } catch (error) {
          console.error({ error });
          this.cartError = "Could not add to cart. Please try again.";
          this.eyePrescriptionOption.isContinueDisabled = false;
          this.a2Submitting = false;
          releaseRxLock(false);
          clearRxButtons();
        } finally {
          if (!getRxLock().inFlight) {
            this.a2Submitting = false;
            clearRxButtons();
          }
        }
      })();
    },
    openPrescription() {
      this.cartError = "";
      this.a2Submitting = false;
      resetRxSession();
      this.skipPrescription = false;
      this.isPrescriptionOpen = true;
      this.clearInputs();
      this.disableScroll();
      window.twoNuSyncPrescriptionDock?.();
      window.setTimeout(() => {
        const close = this.$root.querySelector(".prescription__close");
        close?.focus?.({ preventScroll: true });
      }, 40);
    },
    closePrescription() {
      this.closePrescriptionExitModal();
      this.clearInputs();
      this.resetPrescription();
      this.isPrescriptionOpen = false;
      window.setTimeout(() => {
        this.enableScroll();
      }, 0);
    },
    closePrescriptionAfterSuccess() {
      this.closePrescriptionLaterModal();
      this.closePrescriptionExitModal();
      this.clearInputs();
      this.resetPrescription();
      this.isPrescriptionOpen = false;
      window.setTimeout(() => {
        this.enableScroll();
      }, 0);
    },
    openPrescriptionExitModal() {
      this.closePrescription();
    },
    closePrescriptionExitModal() {
      document.querySelector("[prescription-exit-modal-close-trigger]")?.click();
    },
    openPrescriptionLaterModal() {
      document.querySelector("[prescription-later-modal-open-trigger]")?.click();
      this.disableScroll();
    },
    closePrescriptionLaterModal() {
      document.querySelector("[prescription-later-modal-close-trigger]")?.click();
      this.skipPrescription = false;
    },
    handleSkipPrescription() {
      this.skipPrescription = true;
      this.addToCart();
    },
    setPrescriptionValue(node) {
      const select = node.querySelector("select");
      const value = select?.value;
      if (!value) return;
      if (node.classList.contains("right-sphere-select--js")) this.prescriptionValues.right.sphere = value;
      if (node.classList.contains("right-cylinder-select--js")) this.prescriptionValues.right.cylynder = value;
      if (node.classList.contains("right-axis-select--js")) this.prescriptionValues.right.axis = value;
      if (node.classList.contains("left-sphere-select--js")) this.prescriptionValues.left.sphere = value;
      if (node.classList.contains("left-cylinder-select--js")) this.prescriptionValues.left.cylynder = value;
      if (node.classList.contains("left-axis-select--js")) this.prescriptionValues.left.axis = value;
      if (node.classList.contains("prescription__pd--js")) this.prescriptionValues.pd = value;
    },
    setVariant(value) {
      this.variant = value;
    }
  }));
})();
